<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
    <div class="max-w-2xl mx-auto">
      <!-- Header -->
      <div class="mb-6">
        <router-link 
          :to="'/owner/usuariosadmin'" 
          class="inline-flex items-center text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors">
          <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          Volver a usuarios
        </router-link>
        <h1 class="mt-3 text-3xl font-bold text-gray-900">
          {{ form.id ? 'Editar Usuario' : 'Nuevo Usuario' }}
        </h1>
        <p class="mt-2 text-sm text-gray-600">
          Complete los datos del usuario para {{ form.id ? 'actualizar' : 'crear' }} su cuenta
        </p>
      </div>

      <!-- Form Card -->
      <div class="bg-white rounded-xl shadow-lg overflow-hidden">
        <div class="p-6 sm:p-8">
          <form @submit.prevent="submitForm" class="space-y-6">
            <!-- Email -->
            <div>
              <label for="email" class="block text-sm font-semibold text-gray-700 mb-2">
                Email / Usuario *
              </label>
              <input 
                type="email"
                id="email" 
                v-model="form.email"
                required
                :class="{ 'border-red-500 ring-red-200': errors.email }"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                placeholder="usuario@ejemplo.com"
              />
              <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
            </div>

            <!-- Nombre -->
            <div>
              <label for="nombre" class="block text-sm font-semibold text-gray-700 mb-2">
                Nombre Completo *
              </label>
              <input 
                type="text"
                id="nombre" 
                v-model="form.nombre"
                required
                :class="{ 'border-red-500 ring-red-200': errors.nombre }"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                placeholder="Juan Pérez"
              />
              <p v-if="errors.nombre" class="mt-1 text-sm text-red-600">{{ errors.nombre }}</p>
            </div>

            <!-- Teléfono -->
            <div>
              <label for="telefono" class="block text-sm font-semibold text-gray-700 mb-2">
                Teléfono / Celular *
              </label>
              <input 
                type="tel"
                id="telefono" 
                v-model="form.telefono"
                required
                :class="{ 'border-red-500 ring-red-200': errors.telefono }"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                placeholder="3544123456"
              />
              <p v-if="errors.telefono" class="mt-1 text-sm text-red-600">{{ errors.telefono }}</p>
            </div>

            <!-- Empresa -->
            <div>
              <label for="empresa" class="block text-sm font-semibold text-gray-700 mb-2">
                Empresa / Tenant *
              </label>
              <select
                id="empresa" 
                v-model="form.tenantId"
                required
                :class="{ 'border-red-500 ring-red-200': errors.tenantId }"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors bg-white">
                <option value="" disabled>Seleccione una empresa</option>
                <option v-for="empresa in empresas" :key="empresa.id" :value="empresa.id">
                  {{ empresa.name }}
                </option>
              </select>
              <p v-if="errors.tenantId" class="mt-1 text-sm text-red-600">{{ errors.tenantId }}</p>
            </div>

            <!-- Rol -->
            <div>
              <label for="role" class="block text-sm font-semibold text-gray-700 mb-2">
                Rol de Usuario *
              </label>
              <select
                id="role" 
                v-model="form.role"
                required
                :class="{ 'border-red-500 ring-red-200': errors.role }"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors bg-white">
                <option value="" disabled>Seleccione un rol</option>
                <option v-for="role in roles" :key="role" :value="role">
                  {{ role.charAt(0).toUpperCase() + role.slice(1) }}
                </option>
              </select>
              <p v-if="errors.role" class="mt-1 text-sm text-red-600">{{ errors.role }}</p>
            </div>

            <!-- Passwords Section -->
            <div v-if="!form.id" class="pt-4 border-t border-gray-200">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Credenciales de Acceso</h3>
              
              <!-- Password -->
              <div class="mb-4">
                <label for="password" class="block text-sm font-semibold text-gray-700 mb-2">
                  Contraseña *
                </label>
                <div class="relative">
                  <input 
                    :type="showPassword ? 'text' : 'password'"
                    id="password" 
                    v-model="form.password"
                    :required="!form.id"
                    @input="validatePassword"
                    :class="{ 'border-red-500 ring-red-200': errors.password }"
                    class="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="Mínimo 8 caracteres"
                  />
                  <button
                    type="button"
                    @click="showPassword = !showPassword"
                    class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                    <svg v-if="!showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                    <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                    </svg>
                  </button>
                </div>
                <p v-if="errors.password" class="mt-1 text-sm text-red-600">{{ errors.password }}</p>
                
                <!-- Password Requirements -->
                <div class="mt-2 space-y-1">
                  <p class="text-xs text-gray-600">La contraseña debe contener:</p>
                  <ul class="text-xs space-y-1 ml-4">
                    <li :class="passwordValidation.hasMinLength ? 'text-emerald-600' : 'text-gray-500'">
                      <span v-if="passwordValidation.hasMinLength">✓</span>
                      <span v-else>•</span>
                      Mínimo 8 caracteres
                    </li>
                    <li :class="passwordValidation.hasUpperCase ? 'text-emerald-600' : 'text-gray-500'">
                      <span v-if="passwordValidation.hasUpperCase">✓</span>
                      <span v-else>•</span>
                      Al menos una mayúscula
                    </li>
                    <li :class="passwordValidation.hasNumber ? 'text-emerald-600' : 'text-gray-500'">
                      <span v-if="passwordValidation.hasNumber">✓</span>
                      <span v-else>•</span>
                      Al menos un número
                    </li>
                    <li :class="passwordValidation.hasSpecialChar ? 'text-emerald-600' : 'text-gray-500'">
                      <span v-if="passwordValidation.hasSpecialChar">✓</span>
                      <span v-else>•</span>
                      Al menos un carácter especial (.!@#$%^&*)
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Confirm Password -->
              <div>
                <label for="confirmPassword" class="block text-sm font-semibold text-gray-700 mb-2">
                  Confirmar Contraseña *
                </label>
                <div class="relative">
                  <input 
                    :type="showConfirmPassword ? 'text' : 'password'"
                    id="confirmPassword" 
                    v-model="form.confirmPassword"
                    :required="!form.id"
                    :class="{ 'border-red-500 ring-red-200': errors.confirmPassword }"
                    class="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="Repita la contraseña"
                  />
                  <button
                    type="button"
                    @click="showConfirmPassword = !showConfirmPassword"
                    class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                    <svg v-if="!showConfirmPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                    <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                    </svg>
                  </button>
                </div>
                <p v-if="errors.confirmPassword" class="mt-1 text-sm text-red-600">{{ errors.confirmPassword }}</p>
              </div>
            </div>

            <!-- Success/Error Messages -->
            <div v-if="successMessage" class="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p class="text-sm text-emerald-800">{{ successMessage }}</p>
            </div>
            <div v-if="errorMessage" class="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p class="text-sm text-red-800">{{ errorMessage }}</p>
            </div>

            <!-- Form Actions -->
            <div class="flex gap-3 pt-6 border-t border-gray-200">
              <button 
                type="submit"
                :disabled="isSubmitting"
                class="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center">
                <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ isSubmitting ? 'Guardando...' : (form.id ? 'Actualizar Usuario' : 'Crear Usuario') }}
              </button>
              <router-link
                :to="'/owner/usuariosadmin'"
                class="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-200">
                Cancelar
              </router-link>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>



<script>
import { ref, reactive, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import userService from "../../../composables/api/userService";
import empresaService from "../../../composables/api/empresaService";

export default {
  setup() {
    const router = useRouter();
    const isAdmin = ref(false);
    const empresas = ref([]);
    const roles = ref(["admin", "user"]);
    
    // Form state
    const form = ref({
      id: null,
      email: '',
      nombre: '',
      telefono: '',
      tenantId: '',
      role: '',
      password: '',
      confirmPassword: ''
    });

    // UI state
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);
    const isSubmitting = ref(false);
    const successMessage = ref('');
    const errorMessage = ref('');
    
    // Validation errors
    const errors = reactive({
      email: '',
      nombre: '',
      telefono: '',
      tenantId: '',
      role: '',
      password: '',
      confirmPassword: ''
    });

    // Password validation state
    const passwordValidation = computed(() => ({
      hasMinLength: form.value.password.length >= 8,
      hasUpperCase: /[A-Z]/.test(form.value.password),
      hasNumber: /\d/.test(form.value.password),
      hasSpecialChar: /[.!@#$%^&*]/.test(form.value.password)
    }));

    // Clear all errors
    const clearErrors = () => {
      Object.keys(errors).forEach(key => errors[key] = '');
      successMessage.value = '';
      errorMessage.value = '';
    };

    // Validate form
    const validateForm = () => {
      clearErrors();
      let isValid = true;

      // Email validation
      if (!form.value.email || form.value.email.trim() === '') {
        errors.email = 'El email es requerido';
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
        errors.email = 'El email no es válido';
        isValid = false;
      }

      // Nombre validation
      if (!form.value.nombre || form.value.nombre.trim() === '') {
        errors.nombre = 'El nombre es requerido';
        isValid = false;
      }

      // Teléfono validation
      if (!form.value.telefono || form.value.telefono.trim() === '') {
        errors.telefono = 'El teléfono es requerido';
        isValid = false;
      }

      // Tenant validation
      if (!form.value.tenantId) {
        errors.tenantId = 'Debe seleccionar una empresa';
        isValid = false;
      }

      // Role validation
      if (!form.value.role) {
        errors.role = 'Debe seleccionar un rol';
        isValid = false;
      }

      // Password validation (only for new users)
      if (!form.value.id) {
        if (!form.value.password || form.value.password.trim() === '') {
          errors.password = 'La contraseña es requerida';
          isValid = false;
        } else if (!passwordValidation.value.hasMinLength || 
                   !passwordValidation.value.hasUpperCase || 
                   !passwordValidation.value.hasNumber || 
                   !passwordValidation.value.hasSpecialChar) {
          errors.password = 'La contraseña no cumple los requisitos';
          isValid = false;
        }

        // Confirm password validation
        if (!form.value.confirmPassword || form.value.confirmPassword.trim() === '') {
          errors.confirmPassword = 'Debe confirmar la contraseña';
          isValid = false;
        } else if (form.value.password !== form.value.confirmPassword) {
          errors.confirmPassword = 'Las contraseñas no coinciden';
          isValid = false;
        }
      }

      return isValid;
    };

    // Validate password on input
    const validatePassword = () => {
      if (form.value.password && errors.password) {
        errors.password = '';
      }
      if (form.value.confirmPassword && form.value.password === form.value.confirmPassword) {
        errors.confirmPassword = '';
      }
    };

    // Add user
    const agregarUsuario = async () => {
      const payload = {
        id: form.value.id,
        nombre: form.value.nombre,
        telefono: form.value.telefono,
        celular: form.value.telefono,
        tenantId: form.value.tenantId,
        role: form.value.role,
        password: form.value.password,
        confirmPassword: form.value.confirmPassword,
        email: form.value.email
      };

      try {
        await userService.addUsuario(payload);
        successMessage.value = 'Usuario creado exitosamente';
        setTimeout(() => {
          router.push('/owner/usuariosadmin');
        }, 1500);
      } catch (error) {
        console.error('Error al crear usuario:', error);
        errorMessage.value = error.response?.data?.message || 'Error al crear el usuario. Intente nuevamente.';
      }
    };

    // Edit user
    const editarUsuario = async () => {
      try {
        const payload = {
          UserName: form.value.email,
          Email: form.value.email,
          Nombre: form.value.nombre,
          Telefono: form.value.telefono,
          TenantId: form.value.tenantId,
          Role: form.value.role
        };
        
        await userService.editUsuario(form.value.id, payload);
        successMessage.value = 'Usuario actualizado exitosamente';
        setTimeout(() => {
          router.push('/owner/usuariosadmin');
        }, 1500);
      } catch (error) {
        console.error('Error al editar usuario:', error);
        errorMessage.value = error.response?.data?.message || 'Error al actualizar el usuario. Intente nuevamente.';
      }
    };

    // Submit form
    const submitForm = async () => {
      if (!validateForm()) {
        errorMessage.value = 'Por favor corrija los errores en el formulario';
        return;
      }

      isSubmitting.value = true;
      clearErrors();

      try {
        if (form.value.id == 0 || !form.value.id) {
          await agregarUsuario();
        } else {
          await editarUsuario();
        }
      } catch (error) {
        console.error('Error en submitForm:', error);
      } finally {
        isSubmitting.value = false;
      }
    };


    // Fetch companies
    const fetchEmpresas = async () => {
      try {
        empresas.value = await empresaService.getAllEmpresa();
      } catch (error) {
        console.error('Error al cargar empresas:', error);
        errorMessage.value = 'Error al cargar las empresas';
      }
    };

    // Load user data if editing
    onMounted(async () => {
      await fetchEmpresas();
      
      const query = router.currentRoute.value.query;
      if (query.isAdmin === 'true') {
        isAdmin.value = true;
      }
      
      if (query.id != null) {
        try {
          const userData = await userService.getById(query.id);
          form.value = {
            ...form.value,
            ...userData,
            password: '',
            confirmPassword: ''
          };
        } catch (error) {
          console.error('Error al cargar usuario:', error);
          errorMessage.value = 'Error al cargar los datos del usuario';
        }
      }
    });

    return {
      form,
      empresas,
      roles,
      errors,
      showPassword,
      showConfirmPassword,
      isSubmitting,
      successMessage,
      errorMessage,
      passwordValidation,
      submitForm,
      validatePassword,
      isAdmin
    };
  },
};
</script>